import { useState, useEffect } from 'react';
import { Sparkles, Trophy, Flame, Brain, Heart, Zap, Check, X, ArrowRight, RotateCcw, Star, Target, Gem } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { generateQuizQuestions, getLevelInfo, QuizQuestion } from '@/data/crystalQuizData';
import { crystals } from '@/data/crystalData';

interface LearningProgress {
  total_quizzes_completed: number;
  correct_answers: number;
  streak_days: number;
  xp_earned: number;
  level: number;
  crystals_mastered: string[];
}

export function CrystalLearningGame() {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'result'>('menu');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);
  const [progress, setProgress] = useState<LearningProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading progress:', error);
      }

      setProgress(data || {
        total_quizzes_completed: 0,
        correct_answers: 0,
        streak_days: 0,
        xp_earned: 0,
        level: 1,
        crystals_mastered: []
      });
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    setIsLoading(false);
  };

  const startQuiz = (questionCount: number = 5) => {
    const newQuestions = generateQuizQuestions(questionCount);
    setQuestions(newQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setXpEarned(0);
    setGameState('playing');
  };

  const handleAnswerSelect = async (answer: string) => {
    if (selectedAnswer) return;
    
    setSelectedAnswer(answer);
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answer === currentQuestion.correctAnswer;

    if (isCorrect) {
      setScore(score + 1);
      setXpEarned(xpEarned + currentQuestion.xpValue);
    }

    // Save answer to database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('crystal_quiz_answers').insert({
          user_id: user.id,
          crystal_name: crystals.find(c => c.id === currentQuestion.crystalId)?.name || '',
          question_type: currentQuestion.type,
          question: currentQuestion.question,
          user_answer: answer,
          correct_answer: currentQuestion.correctAnswer,
          is_correct: isCorrect,
          xp_awarded: isCorrect ? currentQuestion.xpValue : 0
        });
      }
    } catch (error) {
      console.error('Error saving answer:', error);
    }

    setShowExplanation(true);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setGameState('result');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update or create progress
      const { data: existingProgress } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const today = new Date().toISOString().split('T')[0];
      const newTotalXp = (existingProgress?.xp_earned || 0) + xpEarned;
      const levelInfo = getLevelInfo(newTotalXp);

      if (existingProgress) {
        await supabase
          .from('crystal_learning_progress')
          .update({
            total_quizzes_completed: existingProgress.total_quizzes_completed + 1,
            correct_answers: existingProgress.correct_answers + score,
            xp_earned: newTotalXp,
            level: levelInfo.level,
            last_quiz_date: today
          })
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('crystal_learning_progress')
          .insert({
            user_id: user.id,
            total_quizzes_completed: 1,
            correct_answers: score,
            xp_earned: xpEarned,
            level: levelInfo.level,
            last_quiz_date: today
          });
      }

      setProgress({
        ...existingProgress,
        total_quizzes_completed: (existingProgress?.total_quizzes_completed || 0) + 1,
        correct_answers: (existingProgress?.correct_answers || 0) + score,
        xp_earned: newTotalXp,
        level: levelInfo.level,
        crystals_mastered: existingProgress?.crystals_mastered || []
      });

    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const levelInfo = progress ? getLevelInfo(progress.xp_earned) : null;

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'practical': return Target;
      case 'psychological': return Brain;
      case 'chakra': return Sparkles;
      case 'property': return Zap;
      default: return Gem;
    }
  };

  const getQuestionLabel = (type: string) => {
    switch (type) {
      case 'practical': return 'Practical';
      case 'psychological': return 'Psychological';
      case 'chakra': return 'Chakra';
      case 'property': return 'Property';
      default: return 'Knowledge';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Sparkles className="h-6 w-6 animate-pulse text-primary" />
        <span className="ml-2 text-muted-foreground">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Overview - Always visible */}
      <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/20">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {levelInfo?.title || 'Crystal Curious'}
                </p>
                <p className="text-xs text-muted-foreground">Level {levelInfo?.level || 1}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-primary">{progress?.xp_earned || 0} XP</p>
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{levelInfo?.xpInCurrentLevel || 0} / {levelInfo?.xpForNextLevel || 100} XP</span>
              <span>Next: {levelInfo?.nextTitle}</span>
            </div>
            <Progress value={levelInfo?.progress || 0} className="h-2" />
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="text-center p-2 bg-background/50 rounded-lg">
              <p className="text-lg font-semibold text-accent">{progress?.total_quizzes_completed || 0}</p>
              <p className="text-xs text-muted-foreground">Quizzes</p>
            </div>
            <div className="text-center p-2 bg-background/50 rounded-lg">
              <p className="text-lg font-semibold text-accent">{progress?.correct_answers || 0}</p>
              <p className="text-xs text-muted-foreground">Correct</p>
            </div>
            <div className="text-center p-2 bg-background/50 rounded-lg">
              <p className="text-lg font-semibold text-orange-500">{progress?.streak_days || 0}</p>
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Flame className="h-3 w-3" /> Streak
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Game States */}
      <AnimatePresence mode="wait">
        {gameState === 'menu' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <Card className="bg-muted/30">
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Brain className="h-6 w-6 text-primary" />
                  Crystal Learning Quiz
                </CardTitle>
                <CardDescription>
                  Learn which crystals help with everyday situations and psychological wellbeing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Card className="bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="h-4 w-4 text-accent" />
                      <span className="text-sm font-medium">Practical</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      "Which crystal for a job interview?"
                    </p>
                  </Card>
                  <Card className="bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Brain className="h-4 w-4 text-secondary" />
                      <span className="text-sm font-medium">Psychological</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      "What helps with anxiety?"
                    </p>
                  </Card>
                </div>

                <div className="space-y-2">
                  <Button 
                    onClick={() => startQuiz(5)} 
                    className="w-full"
                    size="lg"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Quick Quiz (5 questions)
                  </Button>
                  <Button 
                    onClick={() => startQuiz(10)} 
                    variant="outline"
                    className="w-full"
                  >
                    Full Quiz (10 questions)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {gameState === 'playing' && currentQuestion && (
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-4"
          >
            {/* Question Progress */}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="flex items-center gap-1">
                {(() => {
                  const Icon = getQuestionIcon(currentQuestion.type);
                  return <Icon className="h-3 w-3" />;
                })()}
                {getQuestionLabel(currentQuestion.type)}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {currentQuestionIndex + 1} / {questions.length}
              </span>
            </div>
            
            <Progress 
              value={((currentQuestionIndex + 1) / questions.length) * 100} 
              className="h-2"
            />

            {/* Question Card */}
            <Card className="bg-gradient-to-br from-muted/50 to-background border-border/50">
              <CardHeader>
                <CardTitle className="text-lg text-foreground leading-relaxed">
                  {currentQuestion.type === 'practical' && (
                    <span className="text-muted-foreground text-base font-normal block mb-2">
                      Which crystal would you choose if...
                    </span>
                  )}
                  {currentQuestion.question}
                </CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm text-primary font-medium">+{currentQuestion.xpValue} XP</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {currentQuestion.options.map((option, index) => {
                  const isCorrect = option === currentQuestion.correctAnswer;
                  const isSelected = option === selectedAnswer;
                  
                  let buttonClass = 'w-full justify-start text-left p-4 h-auto';
                  if (showExplanation) {
                    if (isCorrect) {
                      buttonClass += ' bg-accent/20 border-accent text-accent hover:bg-accent/20';
                    } else if (isSelected) {
                      buttonClass += ' bg-destructive/20 border-destructive text-destructive hover:bg-destructive/20';
                    }
                  }

                  return (
                    <Button
                      key={index}
                      variant="outline"
                      className={buttonClass}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={!!selectedAnswer}
                    >
                      <span className="flex items-center gap-3 w-full">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="flex-1">{option}</span>
                        {showExplanation && isCorrect && (
                          <Check className="h-5 w-5 text-accent flex-shrink-0" />
                        )}
                        {showExplanation && isSelected && !isCorrect && (
                          <X className="h-5 w-5 text-destructive flex-shrink-0" />
                        )}
                      </span>
                    </Button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Explanation */}
            <AnimatePresence>
              {showExplanation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Card className={`border-2 ${
                    selectedAnswer === currentQuestion.correctAnswer 
                      ? 'bg-accent/10 border-accent/30' 
                      : 'bg-secondary/10 border-secondary/30'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {selectedAnswer === currentQuestion.correctAnswer ? (
                          <div className="p-2 rounded-full bg-accent/20">
                            <Check className="h-4 w-4 text-accent" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-full bg-secondary/20">
                            <Gem className="h-4 w-4 text-secondary" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-foreground mb-1">
                            {selectedAnswer === currentQuestion.correctAnswer 
                              ? 'Correct! +' + currentQuestion.xpValue + ' XP'
                              : 'The answer is ' + currentQuestion.correctAnswer}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {currentQuestion.explanation}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Button onClick={nextQuestion} className="w-full mt-4">
                    {currentQuestionIndex < questions.length - 1 ? (
                      <>
                        Next Question
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        See Results
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {gameState === 'result' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 border-primary/30">
              <CardHeader className="text-center">
                <div className="mx-auto p-4 rounded-full bg-primary/20 w-fit mb-4">
                  <Trophy className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl">Quiz Complete!</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-accent">{score}/{questions.length}</p>
                    <p className="text-sm text-muted-foreground">Correct Answers</p>
                  </Card>
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-primary">+{xpEarned}</p>
                    <p className="text-sm text-muted-foreground">XP Earned</p>
                  </Card>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Accuracy</p>
                  <Progress value={(score / questions.length) * 100} className="h-3 mb-2" />
                  <p className="text-lg font-semibold text-foreground">
                    {Math.round((score / questions.length) * 100)}%
                  </p>
                </div>

                {score === questions.length && (
                  <div className="flex items-center justify-center gap-2 text-accent">
                    <Star className="h-5 w-5 fill-current" />
                    <span className="font-medium">Perfect Score!</span>
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setGameState('menu')}
                    className="flex-1"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Back to Menu
                  </Button>
                  <Button
                    onClick={() => startQuiz(5)}
                    className="flex-1"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Play Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
